import api, { getMediaUrl } from './api';

const TL_BASE = '/trade-license';

export const tradeLicenseApi = {
  // ── Applications ──────────────────────────────────────────────────────────
  getApplications: (params) => api.get(`${TL_BASE}/applications/`, { params }),
  getApplication: (id) => api.get(`${TL_BASE}/applications/${id}/`),
  getPendingApprovals: (params) => api.get(`${TL_BASE}/applications/pending_approvals/`, { params }),
  createApplication: (data) => api.post(`${TL_BASE}/applications/`, data),
  submitApplication: (id) => api.post(`${TL_BASE}/applications/${id}/submit/`),
  publicStatus: (params) => api.get(`${TL_BASE}/applications/public_status/`, { params }),
  translateText: (text, options = {}) =>
    api.post(`${TL_BASE}/translate/`, { text, ...options }),

  // Workflow actions
  beginVerification: (id) => api.post(`${TL_BASE}/applications/${id}/begin_verification/`),
  approveApplication: (id, data = {}) => api.post(`${TL_BASE}/applications/${id}/approve/`, data),
  issueLicense: (id) => api.post(`${TL_BASE}/applications/${id}/issue/`),
  rejectApplication: (id, data) => api.post(`${TL_BASE}/applications/${id}/reject/`, data),
  clarificationApplication: (id, data) =>
    api.post(`${TL_BASE}/applications/${id}/clarification/`, data),
  assignInspection: (id, data) =>
    api.post(`${TL_BASE}/applications/${id}/assign_inspection/`, data),
  getAssignableInspectors: () =>
    api.get(`${TL_BASE}/applications/assignable_inspectors/`),
  getAssignableOfficers: () =>
    api.get(`${TL_BASE}/applications/assignable_officers/`),
  getApprovalHistory: (id) =>
    api.get(`${TL_BASE}/applications/${id}/approval_history/`),
  recordPayment: (id, data) =>
    api.post(`${TL_BASE}/applications/${id}/record_payment/`, data),

  // ── Inspections ───────────────────────────────────────────────────────────
  getInspections: (params) => api.get(`${TL_BASE}/inspections/`, { params }),
  completeInspection: (id, data) =>
    api.post(`${TL_BASE}/inspections/${id}/complete/`, data),

  // ── Documents ─────────────────────────────────────────────────────────────
  getApplicationDocuments: (appId) => api.get(`${TL_BASE}/applications/${appId}/documents/`),
  uploadDocument: (formData) => api.post(`${TL_BASE}/documents/`, formData),
  verifyDocument: (docId, data) => api.post(`${TL_BASE}/documents/${docId}/verify/`, data),

  // ── Licenses ──────────────────────────────────────────────────────────────
  lookupLicense: (licenseNumber) =>
    api.get(`${TL_BASE}/licenses/lookup/`, { params: { license_number: licenseNumber } }),
  renewAsIs: (licenseNumber) =>
    api.post(`${TL_BASE}/licenses/renew-as-is/`, { license_number: licenseNumber }),
  getLicenses: (params) => api.get(`${TL_BASE}/licenses/`, { params }),

  // ── Trade Categories (CRUD) ───────────────────────────────────────────────
  getCategories: () => api.get(`${TL_BASE}/categories/`),
  createCategory: (data) => api.post(`${TL_BASE}/categories/`, data),
  updateCategory: (id, data) => api.patch(`${TL_BASE}/categories/${id}/`, data),
  deleteCategory: (id) => api.delete(`${TL_BASE}/categories/${id}/`),

  // ── Workflow Stages ───────────────────────────────────────────────────────
  getWorkflowStages: () => api.get(`${TL_BASE}/workflow-stages/`),
  createWorkflowStage: (data) => api.post(`${TL_BASE}/workflow-stages/`, data),
  updateWorkflowStage: (id, data) => api.patch(`${TL_BASE}/workflow-stages/${id}/`, data),
  deleteWorkflowStage: (id) => api.delete(`${TL_BASE}/workflow-stages/${id}/`),

  // ── Users/Inspectors ─────────────────────────────────────────────────────
  getPermissionRoles: (params) => api.get(`${TL_BASE}/permission-roles/`, { params }),

  // ── Payments ──────────────────────────────────────────────────────────────
  getPayments: (params) => api.get(`${TL_BASE}/payments/`, { params }),
  verifyPayment: (id) => api.post(`${TL_BASE}/payments/${id}/verify/`),

  // ── Certificate configuration ─────────────────────────────────────────────
  getCertificateConfig: () => api.get(`${TL_BASE}/certificate-config/`),
  saveCertificateConfig: (formData) => api.put(`${TL_BASE}/certificate-config/`, formData),

  // ── Audit and Reports ─────────────────────────────────────────────────────
  getAuditLogs: (params) => api.get(`${TL_BASE}/audit-logs/`, { params }),
  getApplicationSummaryReport: (params) =>
    api.get(`${TL_BASE}/reports/application_summary/`, { params }),
  getRevenueSummaryReport: (params) =>
    api.get(`${TL_BASE}/reports/revenue_summary/`, { params }),
  getApplicationRegisterReport: (params) =>
    api.get(`${TL_BASE}/reports/application_register/`, { params }),
  getPaymentRegisterReport: (params) =>
    api.get(`${TL_BASE}/reports/payment_register/`, { params }),
  getPendingByStageReport: (params) =>
    api.get(`${TL_BASE}/reports/pending_by_stage/`, { params }),

  // ── Applicant portal (OTP login) ──────────────────────────────────────────
  getMyApplications: (params) =>
    api.get(`${TL_BASE}/applications/my_applications/`, { params }),
  getMyApplicationDetail: (id) =>
    api.get(`${TL_BASE}/applications/${id}/my-detail/`),
  respondClarification: (id, response) =>
    api.post(`${TL_BASE}/applications/${id}/respond_clarification/`, { response }),
  submitApplicantPayment: (id, data) =>
    api.post(`${TL_BASE}/applications/${id}/applicant_payment/`, data),
  getMyLicenses: (params) =>
    api.get(`${TL_BASE}/licenses/my_licenses/`, { params }),
};

export const applicantAuthApi = {
  ensureCsrf: () => api.get('/auth/applicant/csrf/'),
  sendLoginOtp: (phone) =>
    api.post('/auth/applicant/send-login-otp/', { phone }),
  sendSignupOtp: (phone, firstName, lastName = '', email = '') =>
    api.post('/auth/applicant/send-signup-otp/', {
      phone,
      first_name: firstName,
      last_name: lastName,
      email,
    }),
  verifyLoginOtp: (phone, otp) =>
    api.post('/auth/applicant/verify-login-otp/', { phone, otp }),
  verifySignupOtp: (phone, otp, profile = {}) =>
    api.post('/auth/applicant/verify-signup-otp/', { phone, otp, ...profile }),
  silentRegister: (data) =>
    api.post('/auth/applicant/silent-register/', data),
  me: () => api.get('/auth/applicant/me/'),
  updateProfile: (data) => api.patch('/auth/applicant/profile/', data),
  logout: () => api.post('/auth/applicant/logout/'),
  // Legacy aliases
  sendOtp: (phone) => api.post('/auth/applicant/send-otp/', { phone }),
  verifyOtp: (phone, otp, profile = {}) =>
    api.post('/auth/applicant/verify-otp/', { phone, otp, ...profile }),
};

export const officeAuthApi = {
  ensureCsrf: () => api.get('/auth/csrf/'),
  login: (username, password) => api.post('/auth/login/', { username, password }),
  me: () => api.get('/auth/me/'),
  logout: () => api.post('/auth/logout/'),
  translate: (text, target = 'hi') => api.post('/auth/translate/', { text, target }),
};

export { getMediaUrl };

export const DOCUMENT_TYPES = [
  { type: 'Aadhaar', name_en: 'Aadhaar Card', name_hi: 'आधार कार्ड', required: true },
  { type: 'PAN', name_en: 'PAN Card', name_hi: 'पैन कार्ड', required: true },
  { type: 'Identity_Proof', name_en: 'Identity Proof', name_hi: 'पहचान प्रमाण', required: true },
  { type: 'GST_Certificate', name_en: 'GST Certificate', name_hi: 'जीएसटी प्रमाणपत्र', required: false },
  { type: 'Property_Ownership', name_en: 'Property Ownership Proof', name_hi: 'संपत्ति स्वामित्व प्रमाण', required: true },
  { type: 'Rent_Agreement', name_en: 'Rent Agreement', name_hi: 'किराया समझौता', required: false },
  { type: 'Holding_Receipt', name_en: 'Holding Tax Receipt', name_hi: 'होल्डिंग कर रसीद', required: true },
  { type: 'Passport_Photo', name_en: 'Passport Size Photo', name_hi: 'पासपोर्ट साइज फोटो', required: true },
  { type: 'Supporting_Document', name_en: 'Additional Supporting Document', name_hi: 'अतिरिक्त सहायक दस्तावेज़', required: false },
];

export const OWNERSHIP_TYPES = [
  { value: 'proprietorship', label: 'Proprietorship', label_hi: 'एकल स्वामित्व' },
  { value: 'partnership', label: 'Partnership', label_hi: 'साझेदारी' },
  { value: 'company', label: 'Company', label_hi: 'कंपनी' },
];

export const APPLICATION_TYPES = [
  { value: 'new_license', label: 'New License', label_hi: 'नया लाइसेंस' },
  { value: 'renewal', label: 'Renewal', label_hi: 'नवीनीकरण' },
];

export const GENDER_OPTIONS = [
  { value: 'male', label: 'Male', label_hi: 'पुरुष' },
  { value: 'female', label: 'Female', label_hi: 'महिला' },
  { value: 'other', label: 'Other', label_hi: 'अन्य' },
];

export const INITIAL_FORM_DATA = {
  applicant_first_name_en: '',
  applicant_first_name_hi: '',
  applicant_gender: '',
  applicant_father_husband_name_en: '',
  applicant_father_husband_name_hi: '',
  business_name_en: '',
  business_name_hi: '',
  trade_category: '',
  business_type: '',
  ownership_type: '',
  holding_number: '',
  ward_number: '',
  address_en: '',
  address_hi: '',
  application_type: 'new_license',
  existing_license_number: '',
  applicant_aadhar: '',
  applicant_pan: '',
  applicant_phone: '',
  applicant_email: '',
  gst_number: '',
  property_owner_name_en: '',
  property_owner_name_hi: '',
  property_owner_address_en: '',
  property_owner_address_hi: '',
};
